import { createContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { CollectionService } from 'lib/api/services/collection.service'
import { countsInitialState, filterInitialState, paginationInitialState } from 'lib/constants/initial-states'
import { compactParams, parseFileName, downloadBlob } from 'lib/utils'
import { useResErrors } from 'lib/hooks'
import { useSnackbar } from 'notistack'
import { AuthService } from '../../lib/api'
import { useAuthStore } from '../AuthStore'

export const HomeStoreContext = createContext({
  homeStore: {},
  homeStoreFunc: {}
})

const HomeStoreProvider = props => {
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const { standardCheck } = useResErrors()
  const _isMounted = useRef(true)

  const { authStore, authStoreFunc } = useAuthStore()

  const [filter, setFilter] = useState(() => {
    const savedFilter = sessionStorage.getItem('filter')
    return savedFilter ? JSON.parse(savedFilter) : filterInitialState
  })

  const [infoCard, setInfoCard] = useState({
    countAll: 0,
    countFiltered: 0
  })

  const [pagination, setPagination] = useState(() => {
    const savedPagination = sessionStorage.getItem('pagination')
    return savedPagination ? JSON.parse(savedPagination) : paginationInitialState
  })

  const [applicationPagination, setApplicationPagination] = useState(() => {
    const saved = sessionStorage.getItem('paginationApplications')
    return saved ? JSON.parse(saved) : paginationInitialState
  })

  const [branches, setBranches] = useState([])
  const [managers, setManagers] = useState([])
  const [contractList, setContractList] = useState(null)

  const [selectedRows, setSelectedRows] = useState([])
  const [selectAll, setSelectAll] = useState(false)

  const [counts, setCounts] = useState(countsInitialState)
  const [statuses, setStatuses] = useState([])
  const [creditProducts, setCreditProducts] = useState([])
  const [applicationList, setApplicationList] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getInitData()
    return () => {
      _isMounted.current = false
    }
  }, [])

  useEffect(() => {
    getOverdueTypeCounts()
  }, [contractList, selectedRows])

  useEffect(() => {
    sessionStorage.setItem('filter', JSON.stringify(filter))
  }, [filter])

  useEffect(() => {
    sessionStorage.setItem('pagination', JSON.stringify(pagination))
  }, [pagination])

  useEffect(() => {
    sessionStorage.setItem('paginationApplications', JSON.stringify(applicationPagination))
  }, [applicationPagination])

  const getInitData = () => {
    getInitContractList()
    getBranches()
    getManagers()
    getCreditProducts()

    if (['/home'].includes(location.pathname) && authStoreFunc.isManager()) {
      getStatuses()
    }
    if (['/tasks'].includes(location.pathname) && authStoreFunc.isAdmin()) {
      getStatuses()
    }
    if (['/applications'].includes(location.pathname) && (authStoreFunc.isAdmin() || authStoreFunc.isManager())) {
      getInitApplicationsList()
    }
  }

  const getApplicationsParams = (
    base = {},
    limit = applicationPagination.limit,
    page = (applicationPagination.offset || 0) + 1
  ) => ({
    ...base,
    limit,
    offset: Math.max((page - 1) * limit, 0)
  })

  const fetchApplications = async params => {
    authStoreFunc.setLoading(true)
    const response = await CollectionService.getApplications(params)
    authStoreFunc.setLoading(false)
    if (response?.data) {
      const list = Array.isArray(response.data?.list)
        ? response.data.list
        : Array.isArray(response.data)
          ? response.data
          : []
      const countFiltered = response.data?.countFiltered ?? list.length
      const countAll = response.data?.countAll ?? countFiltered
      setInfoCard(prev => ({ ...prev, countAll, countFiltered }))
      return { list, count: countFiltered }
    }
    return { list: [], count: 0 }
  }

  const getInitApplicationsList = async () => {
    const res = await fetchApplications(
      getApplicationsParams(getFormattedFilter(), applicationPagination.limit, (applicationPagination.offset || 0) + 1)
    )
    setApplicationList(res.list)
    setApplicationPagination(prev => ({ ...prev, count: res.count }))
  }

  const handleSort = async property => {
    const isSameProperty = filter.sortBy === property
    if (!isSameProperty) {
      await updateSort(property, 'desc')
      return
    }

    const newDirection = filter.sortDirection === 'asc' ? 'desc' : 'asc'
    await updateSort(property, newDirection)
  }

  const updateSort = async (property, direction) => {
    setFilter(prevState => ({
      ...prevState,
      sortBy: property,
      sortDirection: direction
    }))

    const filterParams = getFormattedFilter()

    const requestData = {
      sortBy: property,
      sortDirection: direction
    }

    const response = await getContractList({ requestData, ...filterParams }, pagination.limit, pagination.offset + 1)
    setContractList(response.list)
    setSelectedRows([])
    setSelectAll(false)
  }

  const getArrow = column => {
    const isActiveColumn = filter.sortBy === column
    const color = isActiveColumn ? 'blue' : 'black'

    if (isActiveColumn && filter.sortDirection === 'asc') {
      return <span style={{ color }}>↑</span>
    }
    return <span style={{ color }}>↓</span>
  }

  const getOverdueTypeCounts = () => {
    let OFFBALANCE = 0
    let LEGAL = 0
    let HARD = 0
    let SOFT = 0

    if (!contractList || selectedRows.length === 0) {
      return setCounts(countsInitialState)
    }

    const selectedData = contractList.filter(item => selectedRows.includes(item?.id))

    selectedData.forEach(item => {
      switch (item?.overdueType) {
        case 'OFFBALANCE':
          OFFBALANCE++
          break
        case 'LEGAL':
          LEGAL++
          break
        case 'HARD':
          HARD++
          break
        case 'SOFT':
          SOFT++
          break
        default:
          break
      }
    })

    setCounts({ OFFBALANCE, LEGAL, HARD, SOFT })
  }

  const getStatuses = async () => {
    await CollectionService.getStatuses()
      .then(res =>
        standardCheck(res, () => {
          if (res.data) {
            setStatuses(res.data)
          }
        })
      )
      .finally(() => {})
  }

  const getInitContractList = async () => {
    const res = await getContractList(getFormattedFilter(), pagination.limit, pagination.offset + 1)

    setContractList(res.list)
    setPagination(prevState => ({ ...prevState, count: res.count }))
  }

  const getFormattedFilter = () => {
    const filterParams = {}
    for (const [k, v] of Object.entries(filter)) {
      if (v) filterParams[k] = v
    }
    return filterParams
  }

  const getContractList = async (data, limit = 30, offset = 0) => {
    authStoreFunc.setLoading(true)

    const contractData = {
      ...data,
      limit,
      offset: Math.max((offset - 1) * 10, 0)
    }

    switch (location.pathname) {
      case '/home':
        if (authStoreFunc.isAdmin()) {
          contractData.newOrUnassigned = true
        }
        if (authStoreFunc.isManager()) {
          contractData.wlStatus = 'IN_PROGRESS'
          contractData.assignTo = authStore.user.data.username
        }
        if (authStoreFunc.isLawyer()) {
          contractData.overdueType = 'LEGAL'
        }
        break

      case '/legal':
        if (authStoreFunc.isManager()) {
          contractData.overdueType = 'LEGAL'
          contractData.assignTo = authStore.user.data.username
          contractData.wlStatus = 'IN_PROGRESS_LAWYER'
        }
        if (authStoreFunc.isAdmin()) {
          contractData.overdueType = 'LEGAL'
          contractData.wlStatus = 'IN_PROGRESS_LAWYER'
        }

        break

      case '/tasks':
        if (authStoreFunc.isLawyer()) {
          contractData.wlStatus = 'IN_PROGRESS_LAWYER'
          contractData.legalAssignTo = authStore.user.data.username
        } else {
          contractData.wlStatus = 'IN_PROGRESS'
        }
        break

      case '/fines':
        if (authStoreFunc.isManager()) {
          contractData.overdueType = 'FINE'
          contractData.assignTo = authStore.user.data.username
        }

        if (authStoreFunc.isAdmin()) {
          contractData.overdueType = 'FINE'
        }
        break

      case '/renewable-contracts':
        contractData.wlStatus = 'IN_PROGRESS'
        contractData.hadLegalStatus = true
        break

      case '/complete':
        contractData.completed = true
        contractData.completedByFrom = filter?.completedByFrom
        if (authStoreFunc.isManager()) {
          contractData.assignTo = authStore.user.data.username
        }
        if (authStoreFunc.isLawyer()) {
          contractData.hadLegalStatus = true
          contractData.legalAssignTo = authStore.user.data.username
        }
        break

      default:
        break
    }

    const response = await CollectionService.postCollection(contractData)
    authStoreFunc.setLoading(false)

    if (response?.data?.list) {
      const { list, countFiltered, countAll } = response.data
      setInfoCard(prev => ({
        ...prev,
        countAll,
        countFiltered
      }))
      return { list, count: countFiltered }
    }

    return { list: [], count: 0 }
  }

  const handleSelectAllChange = () => {
    setSelectAll(prevSelectAll => {
      const newSelectAll = !prevSelectAll
      if (newSelectAll) {
        setSelectedRows(contractList.map(item => item?.id))
      } else {
        setSelectedRows([])
      }
      return newSelectAll
    })
  }

  const handleRowCheckboxChange = id => {
    setSelectedRows(prevSelectedRows => {
      const isSelected = prevSelectedRows.includes(id)
      let newSelectedRows
      if (isSelected) {
        newSelectedRows = prevSelectedRows.filter(i => i !== id)
      } else {
        newSelectedRows = [...prevSelectedRows, id]
      }

      if (newSelectedRows?.length === contractList?.length) {
        setSelectAll(true)
      } else {
        setSelectAll(false)
      }

      return newSelectedRows
    })
  }

  const handleSearch = async () => {
    const data = getFormattedFilter()

    if (location.pathname === '/applications' && (authStoreFunc.isAdmin() || authStoreFunc.isManager())) {
      const res = await fetchApplications(getApplicationsParams(data, applicationPagination.limit, 1))
      setApplicationList(res.list)
      setApplicationPagination(prev => ({ ...prev, offset: 0, count: res.count }))
      return
    }

    if (data.clientIin && data.clientIin.length < 6) {
      enqueueSnackbar('Введите минимум 6 цифр в поле ИИН', { variant: 'warning' })
      return
    }

    const res = await getContractList(data, pagination.limit, 0)
    setContractList(res.list)
    setPagination(prevState => ({ ...prevState, offset: 0, count: res.count }))
    setSelectedRows([])
    setSelectAll(false)
  }

  const handlePaginationChange = async (e, val) => {
    if (location.pathname === '/applications' && (authStoreFunc.isAdmin() || authStoreFunc.isManager())) {
      const res = await fetchApplications(getApplicationsParams(getFormattedFilter(), applicationPagination.limit, val))
      setApplicationList(res.list)
      setApplicationPagination(prev => ({ ...prev, count: res.count, offset: val - 1 }))
      return
    }
    const res = await getContractList(getFormattedFilter(), pagination.limit, val)
    setContractList(res.list)
    setPagination(prevState => ({ ...prevState, count: res.count, offset: val - 1 }))
    setSelectedRows([])
    setSelectAll(false)
  }

  const handleClearFilter = async () => {
    setPagination({ ...paginationInitialState })
    setFilter({ ...filterInitialState })

    if (location.pathname === '/applications' && (authStoreFunc.isAdmin() || authStoreFunc.isManager())) {
      setApplicationPagination({ ...paginationInitialState, limit: applicationPagination.limit })
      const res = await fetchApplications(getApplicationsParams({}, applicationPagination.limit, 1))
      setApplicationList(res.list)
      setApplicationPagination(prev => ({ ...prev, offset: 0, count: res.count }))
      setSelectedRows([])
      setSelectAll(false)
      return
    }

    const data = { sortBy: filter.sortBy, sortDirection: filter.sortDirection }
    const res = await getContractList(data, pagination.limit, 0)
    setContractList(res.list)
    setPagination(prevState => ({ ...prevState, count: res.count }))
  }

  const clearState = async () => {
    setPagination({ ...paginationInitialState })
    setFilter({ ...filterInitialState })
    await getContractList({}, pagination.limit, 0)
    setSelectedRows([])
    setSelectAll(false)
  }

  const getManagers = async () => {
    const data = {
      limit: 200,
      isActive: true,
      roleCodes: ['MANAGER'],
      projectCode: 'COLLECTION'
    }

    await AuthService.getUsers(data).then(res => {
      if (res?.data?.userList?.length) {
        setManagers(res?.data?.userList)
      } else {
        setManagers([])
      }
    })
  }

  const getBranches = async () => {
    await CollectionService.getShfSubdivisions().then(res => {
      if (res?.data) {
        setBranches(res?.data?.list)
      }
    })
  }

  const getCreditProducts = async () => {
    await AuthService.getCreditProducts().then(res => {
      if (res?.data) {
        setCreditProducts(res?.data?.list)
      }
    })
  }

  const handleFilterChange = e => {
    const name = e.target.name
    const value = e.target.value

    setFilter(prevState => ({
      ...prevState,
      [name]: name === 'level' && value !== '' ? Number(value) : value
    }))
  }

  const handleFilterDateChange = (e, name) => {
    setFilter(prevState => ({ ...prevState, [name]: e }))
  }

  const handleExport = async (exportParams = {}) => {
    if (loading) return
    setLoading(true)
    try {
      const params = compactParams(exportParams)
      const res = await CollectionService.exportApplications(params)
      if (res && res.data instanceof Blob) {
        const fileName = parseFileName(res.headers) || `applications_${new Date().toISOString().slice(0, 10)}.xlsx`
        downloadBlob(res.data, fileName)
        enqueueSnackbar('Экспорт выполнен', { variant: 'success' })
      } else {
        enqueueSnackbar('Не удалось получить файл', { variant: 'error' })
      }
    } catch (e) {
      const msg = e?.response?.data?.message || 'Ошибка экспорта'
      enqueueSnackbar(msg, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleExportAllWorkList = async (params = {}) => {
    if (loading) return
    setLoading(true)
    try {
      const res = await CollectionService.exportAllWorkList(params)
      if (res && res.data instanceof Blob) {
        const date = params?.date ? `_${params.date}` : ''
        const fileName = parseFileName(res.headers) || `worklist${date}_${new Date().toISOString().slice(0, 10)}.xlsx`
        downloadBlob(res.data, fileName)
        enqueueSnackbar('Экспорт выполнен', { variant: 'success' })
      } else {
        enqueueSnackbar('Не удалось получить файл', { variant: 'error' })
      }
    } catch (e) {
      const msg = e?.response?.data?.message || 'Ошибка экспорта'
      enqueueSnackbar(msg, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleExportWorkListResults = async (params = {}) => {
    if (loading) return
    setLoading(true)
    try {
      const res = await CollectionService.exportWorkListResults(params)
      if (res && res.data instanceof Blob) {
        const date = params?.date ? `_${params.date}` : ''
        const fileName = parseFileName(res.headers) || `results${date}_${new Date().toISOString().slice(0, 10)}.xlsx`
        downloadBlob(res.data, fileName)
        enqueueSnackbar('Экспорт результатов выполнен', { variant: 'success' })
      } else {
        enqueueSnackbar('Не удалось получить файл', { variant: 'error' })
      }
    } catch (e) {
      const msg = e?.response?.data?.message || 'Ошибка экспорта результатов'
      enqueueSnackbar(msg, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const homeStore = {
    contractList,
    branches,
    managers,
    creditProducts,
    filter,
    selectedRows,
    selectAll,
    pagination,
    applicationPagination,
    counts,
    statuses,
    infoCard,
    applicationList,
    loading
  }

  const homeStoreFunc = {
    getManagers,
    getBranches,
    handleFilterChange,
    handleFilterDateChange,
    handleSearch,
    handleClearFilter,
    handleRowCheckboxChange,
    handleSelectAllChange,
    setSelectedRows,
    setSelectAll,
    handlePaginationChange,
    clearState,
    handleSort,
    getArrow,
    handleExport,
    handleExportAllWorkList,
    handleExportWorkListResults
  }

  return <HomeStoreContext.Provider value={{ homeStore, homeStoreFunc }}>{props.children}</HomeStoreContext.Provider>
}
export default HomeStoreProvider
